// Live foreign exchange rates integration using the open ExchangeRate-API endpoint (open.er-api.com).
// Converts and caches rates offline so the app always has working exchange rates even without network.

const CACHE_KEY_PREFIX = "barnyard_fx_rates_";
const DEFAULT_TIMEOUT_MS = 6000;

export interface CachedRates {
  baseCurrency: string;
  rates: Record<string, number>; // in Barnyard format: 1 unit of [CURRENCY] = X units of [baseCurrency]
  timestamp: number; // Date.now() when fetched
  provider: string;
}

/** Fallback benchmark rates to MVR when completely offline without any prior cache. */
const DEFAULT_BENCHMARK_RATES_TO_MVR: Record<string, number> = {
  MYR: 3.52,
  THB: 0.45,
  IDR: 0.00095,
  SGD: 11.85,
  USD: 15.42,
  EUR: 16.85,
  GBP: 19.80,
  JPY: 0.102,
  AED: 4.20,
  SAR: 4.11,
  INR: 0.184,
  LKR: 0.051,
  VND: 0.00062,
  AUD: 10.20,
  CAD: 11.30,
  CHF: 17.60,
  TRY: 0.44,
  PHP: 0.27,
  QAR: 4.23,
  MVR: 1.0,
};

/**
 * Fetches live exchange rates for the given base currency.
 * Attempts server endpoint /api/fx-rates first, then falls back to direct open.er-api.com,
 * and finally to local cache when offline.
 * Returns Barnyard-formatted rates: 1 unit of foreign currency = X units of baseCurrency.
 */
export async function fetchLiveRates(
  baseCurrency: string,
): Promise<{ rates: Record<string, number>; timestamp: number; isFromCache?: boolean }> {
  const base = (baseCurrency || "MVR").toUpperCase();

  let rawData: { result?: string; rates?: Record<string, number> } | null = null;

  // 1. Primary path: Same-origin API endpoint (/api/fx-rates?base=...)
  // Works with zero CSP exceptions and bypasses browser ad-blockers
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
    const res = await fetch(`/api/fx-rates?base=${encodeURIComponent(base)}`, {
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (res.ok) {
      const data = (await res.json()) as { result?: string; rates?: Record<string, number> };
      if (data && data.result === "success" && data.rates) {
        rawData = data;
      }
    }
  } catch {
    // Server endpoint unreachable or offline, attempt direct fallback
  }

  // 2. Direct client fallback to open.er-api.com
  if (!rawData) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
      const res = await fetch(`https://open.er-api.com/v6/latest/${base}`, {
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (res.ok) {
        const data = (await res.json()) as { result?: string; rates?: Record<string, number> };
        if (data && data.result === "success" && data.rates) {
          rawData = data;
        }
      }
    } catch {
      // Direct fetch also unreachable
    }
  }

  // If live rates fetched successfully from either path, convert and save to cache
  if (rawData && rawData.rates) {
    const convertedRates: Record<string, number> = {
      [base]: 1,
    };

    for (const [curr, apiRate] of Object.entries<number>(rawData.rates)) {
      if (curr === base) continue;
      if (typeof apiRate === "number" && apiRate > 0) {
        convertedRates[curr] = 1 / apiRate;
      }
    }

    const payload: CachedRates = {
      baseCurrency: base,
      rates: convertedRates,
      timestamp: Date.now(),
      provider: "open.er-api.com",
    };

    try {
      localStorage.setItem(`${CACHE_KEY_PREFIX}${base}`, JSON.stringify(payload));
    } catch {
      // Ignore quota errors in private browsing
    }

    return { rates: convertedRates, timestamp: payload.timestamp, isFromCache: false };
  }

  // 3. Offline fallback: local cache from previous fetches
  const cached = getCachedRates(base);
  if (cached) {
    return { rates: cached.rates, timestamp: cached.timestamp, isFromCache: true };
  }

  throw new Error("Unable to fetch live rates or load offline cache");
}

/** Reads locally cached rates from previous live fetches. */
export function getCachedRates(baseCurrency: string): CachedRates | null {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${baseCurrency.toUpperCase()}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.rates) return parsed;
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Returns effective exchange rates by combining:
 * 1. Explicit server rates from D1 (highest priority for household ledger accounting).
 * 2. Cached live rates from open.er-api.com.
 * 3. Default benchmark estimates.
 */
export function getEffectiveRates(
  baseCurrency: string,
  serverRates: Record<string, number> = {},
): Record<string, number> {
  const base = (baseCurrency || "MVR").toUpperCase();
  const cached = getCachedRates(base);
  const effective: Record<string, number> = { [base]: 1 };

  // Baseline defaults adjusted for baseCurrency
  const baseToMvr = base === "MVR" ? 1 : (serverRates[base] || DEFAULT_BENCHMARK_RATES_TO_MVR[base] || 1);
  for (const [c, rateToMvr] of Object.entries(DEFAULT_BENCHMARK_RATES_TO_MVR)) {
    effective[c] = rateToMvr / baseToMvr;
  }

  // Overlay cached live rates
  if (cached?.rates) {
    for (const [c, r] of Object.entries(cached.rates)) {
      effective[c] = r;
    }
  }

  // Overlay explicit household D1 rates (authoritative)
  for (const [c, r] of Object.entries(serverRates)) {
    if (r > 0) effective[c] = r;
  }

  effective[base] = 1;
  return effective;
}

/** Computes the exchange rate: 1 unit of fromCurrency = X units of toCurrency. */
export function calculateCrossRate(
  fromCurr: string,
  toCurr: string,
  ratesTable: Record<string, number>,
): number {
  if (fromCurr === toCurr) return 1;
  const fromInBase = ratesTable[fromCurr] || 1;
  const toInBase = ratesTable[toCurr] || 1;
  if (toInBase <= 0) return 1;
  return fromInBase / toInBase;
}

/** Formats how long ago a rate was fetched for user display. */
export function formatRateAge(timestamp: number): string {
  if (!timestamp) return "";
  const diffMs = Date.now() - timestamp;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
